import styled from '@emotion/styled';
import {
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  type DividerProps as MuiDividerProps,
  type MenuItemProps as MuiMenuItemProps,
} from '@mui/material';
import {
  Button,
  ButtonProps,
  IconButton,
  IconButtonProps,
  Text,
  Tooltip,
  TooltipProps,
} from 'components';
import { useRef, useState } from 'react';
import { styledTheme } from 'styles/theme/theme';

export interface MenuItemProps extends Partial<MuiMenuItemProps> {
  id?: string;
  startIcon?: React.ReactNode;
  label?: React.ReactNode;
  isSelected?: boolean;
  tooltip?: React.ReactNode;
}

export interface DividerItemProps extends Partial<MuiDividerProps> {
  id?: string;
  divider: true;
}

export type ActionCellItemProps = MenuItemProps | DividerItemProps;

const isDividerItem = (item: ActionCellItemProps): item is DividerItemProps =>
  item.divider === true;

export interface BaseActionCellProps {
  items?: ActionCellItemProps[];
  value?: MuiMenuItemProps['value'];
  onItemClick?: (item?: MenuItemProps) => void;
  gap?: React.CSSProperties['gap'];
  v1?: boolean;
  paperWidth?: number;
  tooltipTitle?: React.ReactNode;
  tooltipProps?: TooltipProps;
}

export interface ActionCellButtonProps extends BaseActionCellProps {
  triggerProps?: ButtonProps;
  triggerType?: 'button';
}

export interface ActionCellIconButtonProps extends BaseActionCellProps {
  triggerProps?: IconButtonProps;
  triggerType?: 'iconButton';
}

export type ActionCellProps = ActionCellButtonProps | ActionCellIconButtonProps;

export const ActionCell = ({
  triggerProps,
  items,
  onItemClick,
  value = '',
  triggerType = 'button',
  gap = 8,
  v1 = false,
  paperWidth,
  tooltipTitle,
  tooltipProps,
}: ActionCellProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const accessibleName = (triggerProps as { 'aria-label'?: string } | undefined)?.['aria-label'];

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'table-action-cell' : undefined;
  const isActionCellOpen = Boolean(anchorEl);
  const trigger =
    triggerType === 'iconButton' ? (
      <IconButton
        size="md"
        {...(triggerProps as unknown as object)}
        aria-label={accessibleName}
        sx={{
          ...(isActionCellOpen
            ? {
                background: `${styledTheme.palette.bg.brandPrimary} !important`,
                color: `${styledTheme.palette.text.brandSecondary} !important`,
              }
            : {}),
        }}
        aria-describedby={id}
        onClick={e => {
          triggerProps?.onClick?.(e);
          handleClick(e);
        }}
      />
    ) : (
      <Button
        size="sm"
        variant="tertiaryOutline"
        {...(triggerProps as unknown as object)}
        ref={buttonRef}
        aria-describedby={id}
        aria-label={accessibleName}
        onClick={e => {
          triggerProps?.onClick?.(e);
          handleClick(e);
        }}
        sx={{
          ...(!v1 && {
            fontSize: styledTheme.typography.size.xs,
            border: 'none',
            padding: `${styledTheme.s.xs} ${styledTheme.s.sm}`,
            '> div': {
              lineHeight: '16px',
            },
          }),
        }}
      />
    );
  return (
    <>
      {tooltipTitle ? (
        <Tooltip {...tooltipProps} title={tooltipTitle}>
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      <StyledMenu
        id={id}
        open={open}
        anchorEl={anchorEl}
        gap={gap}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            style: {
              width: paperWidth ?? buttonRef.current?.offsetWidth,
            },
          },
        }}
        disableRestoreFocus
      >
        {items?.map((menuItemProps, index) => {
          if (isDividerItem(menuItemProps)) {
            const { divider, id: itemId, ...dividerProps } = menuItemProps;
            return (
              <Divider
                key={itemId ?? `divider-${index}`}
                {...dividerProps}
                style={{ width: '100%', ...dividerProps?.style }}
              />
            );
          }

          const { disabled, ...item } = menuItemProps;
          const menuItem = (
            <StyledMenuItem
              {...item}
              disabled={disabled}
              isSelected={item?.value === value}
              key={item?.id}
              onClick={e => {
                if (!disabled) {
                  item?.onClick?.(e);
                  onItemClick?.(item);
                }
              }}
            >
              {item?.startIcon && <StyledListItemIcon>{item?.startIcon}</StyledListItemIcon>}
              {typeof item?.label === 'string' ? (
                <Text variant="bodyXs" color="secondary">
                  {item?.label}
                </Text>
              ) : (
                item?.label
              )}
            </StyledMenuItem>
          );

          if (item?.tooltip) {
            if (disabled === true) {
              return (
                <Tooltip key={item?.id} title={item?.tooltip}>
                  <span>{menuItem}</span>
                </Tooltip>
              );
            }
            return (
              <Tooltip key={item?.id} title={item?.tooltip}>
                {menuItem}
              </Tooltip>
            );
          }

          return menuItem;
        })}
      </StyledMenu>
    </>
  );
};

type StyledMenuProps = {
  gap?: React.CSSProperties['gap'];
};

const StyledMenu = styled(Menu, {
  shouldForwardProp: prop => prop !== 'gap',
})<StyledMenuProps>(({ theme, gap = 8 }) => ({
  '& .MuiMenu-paper': {
    display: 'flex',
    gap: 8,
    flexDirection: 'column',
    backgroundColor: theme?.palette?.bg?.primary,
    borderRadius: 8,
    border: `0.5px solid ${theme?.palette?.border?.primarySubtle}`,
    boxShadow: '0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03)',
    marginTop: 4,
    alignItems: 'flex-start',
    ul: {
      alignItems: 'flex-start',
      padding: '4px !important',
      width: '100%',
      display: 'flex',
      gap,
      flexDirection: 'column',
      li: {
        width: '100%',
        paddingLeft: '8px !important',
        padding: 8,
        gap: 8,
      },
    },
  },
}));

const StyledListItemIcon = styled(ListItemIcon)`
  min-width: unset !important;
`;

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: prop => prop !== 'isSelected',
})<MenuItemProps>(({ isSelected, theme }) => ({
  backgroundColor: isSelected ? theme?.palette.bg.brandSubtle : 'inherit',
  borderRadius: '6px',
}));
